// Modules
import Head from "next/head";

// Components
import FilterAccordion from "components/searchPage/FilterAccordion/FilterAccordion";

// Hooks
import {useFilters} from "hooks/searchPage/useFilters";
import {useCallback} from "react";

// Redux
import {useAppSelector} from "redux/hooks";
import {categoriesSelector} from "redux/searchPage/categories/categoriesSlice";
import {seoInfoPageSelector} from "redux/searchPage/seoInformations/seoInformationsSlice";
import {activeFiltersSelector, urlPathSelector} from "redux/searchPage/filters/filtersSlice";

// Types
import {Category, CurrentSelectedCategory} from "interfaces/searchPage/category";

// Constants
import {FORMAZIONE_PAGE} from "constants/shared/pages";
import { QueryStringPrimaryKeys } from "constants/searchPage/urlEnum";

// Utils
import {isVisibleTag} from "utils/searchPage/categories";
import {handleNewFilterParameterInPathname} from "utils/searchPage/queryString";

interface FilterProps {
	isFilterOpen: boolean;
	setIsFilterOpen: (isFilterOpen: boolean) => void;
}

const Filters = ({ isFilterOpen, setIsFilterOpen }: FilterProps): JSX.Element => {
	const clearAllFilters = useFilters();
	const seoInfoPage = useAppSelector(seoInfoPageSelector);
	const activeFilters = useAppSelector(activeFiltersSelector);
	const categories = useAppSelector(categoriesSelector);
	const urlPath = useAppSelector(urlPathSelector);

	const getCanonicalUrl = (): string => {
		let relativePathFromParameter: string | undefined;

		if (activeFilters.length) {
			relativePathFromParameter = handleNewFilterParameterInPathname(activeFilters[activeFilters.length - 1], urlPath, categories);
		}

		const baseUrl = `${process.env.NEXT_PUBLIC_DOMAIN}${FORMAZIONE_PAGE}`;
		const isValidRelativePath = relativePathFromParameter && !relativePathFromParameter.includes('undefined');
		const relativePath = isValidRelativePath ? `/${relativePathFromParameter}` : "";
		const fullPath = `${baseUrl}${relativePath}`;

		const urlFromParameter = new URL(fullPath);

		const requestedUrl = new URL(urlPath ? `${baseUrl}/${urlPath}` : baseUrl);

		const searchParams = new URLSearchParams(requestedUrl.search);
		const pageQueryParam = searchParams.get(QueryStringPrimaryKeys.PAGE);

		return pageQueryParam !== null ? `${urlFromParameter.href}?${QueryStringPrimaryKeys.PAGE}=${pageQueryParam}` : urlFromParameter.href;
	};


	// The following function must be left here in order to have the correct opened macrocategories at the page loading
	const FilterAccordionArray = useCallback(() => {
		const buildActiveFilterCategory = (macroCategory: Category): CurrentSelectedCategory => {
			const activeCategoryByParameter = activeFilters.find((activeFilter) => activeFilter?.macroCategory?.urlParameter === macroCategory?.urlParameter) || {
				macroCategory,
				selectedTags: []
			};
			const updateSelectedTags = activeCategoryByParameter.selectedTags.filter((tag) => tag.urlParameter === macroCategory.urlParameter);
			return {...activeCategoryByParameter, selectedTags: updateSelectedTags};
		};

		return categories.filter(tag => isVisibleTag(tag))
							.map((category) => {
			const activeFilterCategory = buildActiveFilterCategory(category);
			return <FilterAccordion key={category.urlParameter} activeFiltersByMacro={activeFilterCategory} />;
		});
	}, [activeFilters, categories]);

	return (
		<div className={`wk-listing-page-left-content ${isFilterOpen ? "wk-mobile-filter" : ""}`}>
			<Head>
				<title>{seoInfoPage.title ? `${seoInfoPage.title} | Ipsoa Formazione` : "Ipsoa Formazione"}</title>
				<meta
					name="description"
					content={
						seoInfoPage.metaDescription ||
						(seoInfoPage.title?.length
							? `${seoInfoPage.title} della Scuola di Formazione Ipsoa: assolvi gli obblighi formativi e dai valore al futuro e alla tua professione.`
							: "Scuola di Formazione Ipsoa: assolvi gli obblighi formativi e dai valore al futuro e alla tua professione.")
					}
				/>
				<link rel="canonical" href={getCanonicalUrl()}/>
			</Head>
			<h3 className="wk-lp-title">Filtri</h3>
			<div className="wk-filter-accordion">
				{FilterAccordionArray()}
				<div className="wk-button" style={{ cursor: "pointer" }} role="button" tabIndex={0} onClick={clearAllFilters}>
					Rimuovi tutti i filtri
				</div>
			</div>
			<span className={`wk-icon-close ${isFilterOpen ? "" : "wk-hidden"}`} onClick={() => setIsFilterOpen(!isFilterOpen)} />
		</div>
	);
};

export default Filters;
